'use client';

// MANUAL SUPABASE STEP REQUIRED — run before this feature will work:
// CREATE TABLE IF NOT EXISTS saved_jobs (
//   id uuid DEFAULT gen_random_uuid() PRIMARY KEY,
//   user_id uuid REFERENCES profiles(id) ON DELETE CASCADE,
//   job_id uuid REFERENCES job_postings(id) ON DELETE CASCADE,
//   created_at timestamptz DEFAULT now(),
//   UNIQUE(user_id, job_id)
// );
// ALTER TABLE saved_jobs ENABLE ROW LEVEL SECURITY;
// CREATE POLICY "Users can save jobs" ON saved_jobs
//   FOR INSERT TO authenticated WITH CHECK (auth.uid() = user_id);
// CREATE POLICY "Users can view own saved jobs" ON saved_jobs
//   FOR SELECT USING (auth.uid() = user_id);
// CREATE POLICY "Users can unsave jobs" ON saved_jobs
//   FOR DELETE USING (auth.uid() = user_id);

import { useEffect, useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import {
  Bookmark,
  Send,
  Briefcase,
  MapPin,
  DollarSign,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { createClient } from '@/lib/supabase/browser';
import { getJobStatus } from '@/lib/jobStatus';
import { Card, Tag, Button as DsButton } from '@/components/ds';
import ShareJobButton from '@/components/ShareJobButton';

export interface JobCardJob {
  id: string;
  title: string;
  description?: string;
  company?: string;
  job_type?: string;
  location?: string;
  rate?: string;
  tags?: string[];
  audition_lines?: (string | { character_name?: string; lines?: string[] })[];
  poster?: { id: string; display_name?: string; username?: string; avatar_url?: string } | null;
  is_filled?: boolean | null;
  is_archived?: boolean | null;
  post_removal_date?: string | null;
}

function getPreviewLines(auditionLines: JobCardJob['audition_lines']): string[] {
  if (!auditionLines?.length) return [];
  const out: string[] = [];
  for (const item of auditionLines.slice(0, 5)) {
    if (typeof item === 'string' && item.trim()) out.push(item.trim());
    if (typeof item === 'object' && item !== null && 'character_name' in item) {
      const name = (item as { character_name?: string }).character_name;
      const lines = (item as { lines?: string[] }).lines;
      if (name) out.push(name);
      else if (lines?.length) out.push(lines[0]);
    }
  }
  return out.slice(0, 3);
}

interface JobCardProps {
  job: JobCardJob;
  applicationCount?: number;
  className?: string;
  onSaveForLater?: (job: JobCardJob) => void;
}

const DESCRIPTION_LINES = 3;

export default function JobCard({ job, className, onSaveForLater }: JobCardProps) {
  const router = useRouter();
  const [expanded, setExpanded] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const roleInitial = (job.title || '?').charAt(0).toUpperCase();
  const roleImageUrl = null;
  const isPaid = !!job.rate && job.rate.trim().length > 0;
  const { canApply } = getJobStatus(job);
  const lines = getPreviewLines(job.audition_lines);
  const description = job.description || '';
  const lineCount = description.split('\n').filter(Boolean).length;
  const showReadMore = !expanded && (lineCount > DESCRIPTION_LINES || description.length > 200);
  const displayDescription = showReadMore
    ? description.slice(0, 200) + (description.length > 200 ? '...' : '')
    : description;
  const jobId = job.id;

  useEffect(() => {
    const checkSaved = async () => {
      const supabase = createClient();
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return;
      const { data } = await supabase
        .from('saved_jobs')
        .select('id')
        .eq('user_id', user.id)
        .eq('job_id', jobId)
        .maybeSingle();
      setIsSaved(!!data);
    };
    checkSaved().catch((error) => {
      console.error('Error checking saved state:', error);
    });
  }, [jobId]);

  const handleSave = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (saving) return;

    const supabase = createClient();
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) {
      router.push('/login?redirect=' + encodeURIComponent(window.location.pathname));
      return;
    }

    try {
      setSaving(true);
      setSaveError(null);

      if (isSaved) {
        const { error } = await supabase
          .from('saved_jobs')
          .delete()
          .eq('user_id', user.id)
          .eq('job_id', jobId);

        if (error) {
          console.error('[SaveJob] Delete error:', JSON.stringify(error));
          setSaveError('Could not unsave. Please try again.');
          return;
        }
        setIsSaved(false);
      } else {
        const { error } = await supabase
          .from('saved_jobs')
          .insert({ user_id: user.id, job_id: jobId });

        if (error) {
          console.error('[SaveJob] Insert error:', JSON.stringify(error));
          if ((error as any).code === '23505') {
            setIsSaved(true);
            return;
          }
          if ((error as any).code === '42501' || error.message?.includes('policy')) {
            setSaveError('Permission denied. Please sign out and back in.');
            return;
          }
          setSaveError(`Could not save: ${error.message}`);
          return;
        }
        setIsSaved(true);
      }
      onSaveForLater?.(job);
    } catch (err) {
      console.error('[SaveJob] Unexpected error:', err);
      setSaveError('Something went wrong. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card
      hoverable
      padded={false}
      className={cn('overflow-hidden', className)}
    >
      <div className="p-6">
        <Link href={`/jobs/${job.id}`} className="block mb-4">
          <div className="flex items-start gap-4">
            {/* Role/character image or initial placeholder */}
            <div className="flex-shrink-0 w-14 h-14 rounded-md overflow-hidden bg-surface-3 flex items-center justify-center">
              {roleImageUrl ? (
                <Image
                  src={roleImageUrl}
                  alt=""
                  width={56}
                  height={56}
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="text-2xl font-bold text-text-secondary">{roleInitial}</span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-base font-semibold text-text-primary truncate">{job.title}</h3>
              {job.company && (
                <p className="text-text-secondary text-xs mt-0.5">{job.company}</p>
              )}
              <div className="mt-2">
                <Tag variant={isPaid ? 'accent' : 'default'}>
                  {isPaid ? 'Paid' : 'Unpaid'}
                </Tag>
              </div>
              <div className="flex flex-wrap gap-3 mt-2 text-xs text-text-secondary">
                {job.job_type && (
                  <span className="inline-flex items-center gap-1.5">
                    <Briefcase className="w-3.5 h-3.5" strokeWidth={1.75} />
                    {job.job_type}
                  </span>
                )}
                {job.location && (
                  <span className="inline-flex items-center gap-1.5">
                    <MapPin className="w-3.5 h-3.5" strokeWidth={1.75} />
                    {job.location}
                  </span>
                )}
                {job.rate && (
                  <span className="inline-flex items-center gap-1.5">
                    <DollarSign className="w-3.5 h-3.5" strokeWidth={1.75} />
                    <span className="text-text-primary font-medium tabular-nums">{job.rate}</span>
                  </span>
                )}
              </div>
            </div>
          </div>
        </Link>

        {/* Role description — truncated to 3 lines with read more expand */}
        <div className="mb-4">
          <p
            className={cn(
              'text-text-secondary text-sm whitespace-pre-line',
              showReadMore && 'line-clamp-3'
            )}
          >
            {expanded ? description : displayDescription}
          </p>
          {showReadMore && (
            <button
              type="button"
              className="text-accent text-sm hover:text-accent-hover mt-1"
              onClick={(e) => {
                e.preventDefault();
                setExpanded(true);
              }}
            >
              Read more
            </button>
          )}
        </div>

        {/* Voice tags */}
        {job.tags && job.tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-4">
            {job.tags.slice(0, 8).map((tag) => (
              <Tag key={tag}>{tag}</Tag>
            ))}
          </div>
        )}

        {/* Audition lines (up to 3) */}
        {lines.length > 0 && (
          <div className="space-y-1.5 mb-4">
            {lines.map((line, i) => (
              <div
                key={i}
                className="text-text-secondary text-sm py-1.5 px-3 bg-surface-3 rounded-sm border border-border-subtle"
              >
                {line}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Action buttons — prevent navigation when clicking buttons */}
      <div className="px-6 pb-6 pt-0 flex flex-wrap items-center gap-2">
        <DsButton
          type="button"
          variant="secondary"
          size="sm"
          onClick={handleSave}
          disabled={saving}
          className={cn(isSaved && 'text-accent border-accent')}
        >
          <Bookmark
            className="w-3.5 h-3.5"
            strokeWidth={1.75}
            fill={isSaved ? 'currentColor' : 'none'}
          />
          {isSaved ? 'Saved' : 'Save for Later'}
        </DsButton>
        {saveError && <p className="w-full text-status-busy text-xs mt-1">{saveError}</p>}
        {canApply ? (
          <Link href={`/jobs/${job.id}`}>
            <DsButton variant="primary" size="sm">
              <Send className="w-3.5 h-3.5" strokeWidth={1.75} />
              Apply / Audition
            </DsButton>
          </Link>
        ) : (
          <Link href={`/jobs/${job.id}`}>
            <DsButton variant="ghost" size="sm">
              View Job
            </DsButton>
          </Link>
        )}
        <ShareJobButton jobId={job.id} jobTitle={job.title} variant="button" size="sm" />
      </div>
    </Card>
  );
}
